// src/components/ServicesSection.jsx
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Wrench,
  Building2,
  Sparkles,
  Home,
  ClipboardCheck,
  Hammer,
} from "lucide-react";

const services = [
  {
    id: 1,
    title: "Architectural Design",
    description:
      "Modern, functional, and elegant architectural designs tailored to your lifestyle.",
    icon: Building2,
  },
  {
    id: 2,
    title: "Building & Construction",
    description:
      "High-quality construction with attention to detail, durability, and premium finishes.Comprehensive building solutions with high-quality materials and expert craftsmanship.",
    icon: Wrench,
  },
  {
    id: 3,
    title: "Interior Design",
    description:
      "Luxury interiors that combine aesthetics with comfort to create timeless spaces.",
    icon: Sparkles,
  },
  {
    id: 4,
    title: "Smart Homes",
    description:
      "Integrating modern technology to make your living spaces smarter, safer, and more efficient.",
    icon: Home,
  },
  {
    id: 5,
    title: "Consulting",
    description:
      "Expert advice and professional guidance to bring clarity and confidence to your projects.",
    icon: ClipboardCheck,
  },
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.25 },
  },
};

const item = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

export default function ServicesSection() {
  const [offsetY, setOffsetY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setOffsetY(window.scrollY * 0.15);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section className="relative bg-gray-50 py-24 overflow-hidden">
      {/* Background parallax overlay */}
      <motion.div
        className="absolute inset-0"
        style={{ transform: `translateY(${offsetY}px)` }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10 pointer-events-none"
          style={{ backgroundImage: "url('/luxeheights2.jpg')" }}
        />
      </motion.div>

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Title */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="text-center mb-16"
        >
          <motion.h2
            variants={item}
            className="text-3xl md:text-4xl font-bold text-gray-900"
          >
            Our Services
          </motion.h2>
          <motion.p
            variants={item}
            className="mt-4 text-gray-600 max-w-2xl mx-auto"
          >
            From concept to completion, we provide a full range of services to
            bring your dream home to life.
          </motion.p>
        </motion.div>

        {/* Services grid */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="grid md:grid-cols-3 gap-10"
        >
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.id}
                variants={item}
                whileHover={{ y: -8 }}
                className="relative group rounded-2xl cursor-pointer"
              >
                {/* Glowing border effect */}
                <div className="absolute inset-0 rounded-2xl border border-transparent group-hover:border-blue-400 group-hover:shadow-[0_0_25px_rgba(59,130,246,0.6)] transition-all duration-500 pointer-events-none"></div>

                {/* Card Content */}
                <div className="relative z-10 bg-white/80 backdrop-blur-sm p-10 rounded-2xl shadow transition duration-300">
                  {/* Icon */}
                  <motion.div
                    className="mb-6 flex items-center justify-center"
                    whileHover={{
                      scale: 1.15,
                      rotate: 5,
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <Icon className="w-12 h-12 text-blue-600 group-hover:text-blue-700 transition-colors duration-300" />
                  </motion.div>

                  {/* Title */}
                  <motion.h3
                    className="text-xl font-semibold text-gray-900 transition-colors duration-300 group-hover:text-blue-700"
                    whileHover={{ opacity: 1 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                  >
                    {service.title}
                  </motion.h3>

                  {/* Description */}
                  <motion.p
                    className="mt-4 text-gray-600 opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                    whileHover={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                  >
                    {service.description}
                  </motion.p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      {/* Diagonal divider into next section */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0] rotate-180">
        <svg
          className="relative block w-[calc(100%+1.3px)] h-20"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          viewBox="0 0 1200 120"
        >
          <path
            d="M1200 0L0 0 892.25 114.72 1200 0z"
            className="fill-gray-50"
          ></path>
        </svg>
      </div>
    </section>
  );
}
